import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Toast } from 'primereact/toast';
import PrimeReactButton from '../../atoms/Button/ButtonAtom';
import PrimeReactSingleSelect from '../../atoms/single-select-dropdown/single-select-dropdown';
import './user-identity.scss';
import { useDescope, useSession } from '@descope/react-sdk';
import { useActiveOrganization } from '@center-technologies/shared-api-clients';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import { setUserId, setFavoriteUser } from '@center-pwa/reducers/userProfileReducer';
import UploadAvatarDialogMolecule from './UploadAvatarDialogMolecule';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import { globalSphereCache, uploadSphereAvatar } from '@center-platform/spheres/cache';
import { setDefaultUserForIdentity } from '@center-platform/spheres/identity';
import { useNavigate, useParams } from 'react-router-dom';
import FavoriteSelectorAtom from '../../atoms/favorite-selector/FavoriteSelectorAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useIdentityPreferences, Theme } from '../../../hooks/useIdentityPreferences';
import { useSpherePermission, resetSpherePermissionCache } from '../../../hooks/useSpherePermission';
import { resetPlatformAdminCache } from '../../../hooks/usePlatformAdmin';
import { resetOrgAdminCache } from '../../../hooks/useOrgAdmin';
import { FALLBACK_METASPACE_SYSTEM_NAME } from '../../../constants/metaspaces';
import { saveIdentitySelection, fetchMemberOrganizationIds, type IdentitySelection } from '../../../utils/identitySelection';
import { useTranslation } from 'localization';
import type { GlyphColor } from '../../../types/glyph';

interface Option {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  /**
   * Organization id of the user this option represents.
   * Absent for the metasphere identity row (which is not org-scoped) and for
   * users whose identity record has no associated organization. Selecting an
   * option with no org id is identity-level / metasphere browsing — the active
   * organization is cleared, so no organization header is sent and only
   * identity-level routes answer.
   */
  organizationId?: string;
}

/** The fields the org auto-switch reads from the sphere on the URL. */
export interface UrlSphereHint {
  id?: string;
  properties?: { type?: string; sphereId?: string };
}

interface UserIdentityProps {
  identityId: string;
  /** Reports whether the identity selector is still resolving, so the page can hold
   *  the metaspace back until the header is populated. */
  onLoadingChange?: (loading: boolean) => void;
  /** The sphere on the URL, when the parent already holds it. Saves the org
   *  auto-switch its own fetch of the same sphere the page just loaded. */
  urlSphere?: UrlSphereHint | null;
}

const UserIdentity: React.FC<UserIdentityProps> = ({ identityId, onLoadingChange, urlSphere }) => {
  // All hooks must be called before any conditional returns
  const { logout } = useDescope();
  const { setActiveOrganization } = useActiveOrganization();
  const { claims } = useSession();
  // Descope mints `act.sub` (actor) alongside `sub` (target) for an impersonated
  // session; its presence is the impersonation signal, matching backend
  // normaliseClaims' actorIdentityId derivation.
  const actorIdentityId = (claims as { act?: { sub?: string } } | undefined)?.act?.sub;
  const targetIdentityId = (claims as { sub?: string } | undefined)?.sub;
  const isImpersonating = !!actorIdentityId;
  const { t } = useTranslation();
  const isFetchedRef = useRef(false);
  // Tracks the last org we auto-switched accounts for (keyed by org id), so the
  // URL-org auto-switch fires once per org and can't loop or fight a manual switch.
  const autoSwitchedOrgRef = useRef<string | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedIdentity, setSelectedIdentity] = useState<Option | null>(null);
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const [metasphereIdentity, setMetasphereIdentity] = useState<string | null>(null);
  const [isFavoriteSelected, setIsFavoriteSelected] = useState(false);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { sphereId: urlSphereId } = useParams<{ sphereId: string }>();
  const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { theme, setTheme } = useIdentityPreferences();
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const apiClients = useApiClientsOrNull();


  // Resolves via the identity-keyed batch coalescer (POST /spheres/permissions).
  const { allowed: hasUploadPermission } = useSpherePermission(userId ?? undefined, 'edit');

  // Memoize the endpoint
  const endpoint = useMemo(() => {
    if (userId && identityId) {
      return `/spheres/${identityId}`;
    }
    return null;
  }, [userId, identityId]);

  // Initialize and listen for theme changes from the document
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Initialize theme state
      const currentTheme = document.documentElement.dataset.theme;
      setIsDarkTheme(currentTheme === 'dark');

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            const updatedTheme = document.documentElement.dataset.theme;
            setIsDarkTheme(updatedTheme === 'dark');
          }
        });
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });

      return () => observer.disconnect();
    }
  }, []);

  // Apply theme preferences when they change
  useEffect(() => {
    if (typeof document !== 'undefined') {
      let effectiveTheme: 'light' | 'dark';

      if (theme === 'system') {
        // Use system preference
        effectiveTheme = globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else {
        effectiveTheme = theme;
      }

      console.log(`🎨 User Identity: Applying theme ${effectiveTheme} (preference: ${theme})`);

      document.documentElement.dataset.theme = effectiveTheme;
      document.body.dataset.theme = effectiveTheme;
    }
  }, [theme]);

  const toggleTheme = async () => {
    const currentEffectiveTheme = isDarkTheme ? 'dark' : 'light';
    const newTheme: Theme = currentEffectiveTheme === 'dark' ? 'light' : 'dark';

    console.log(`🎨 User Identity: Toggling theme from ${currentEffectiveTheme} to ${newTheme}`);

    // Update localStorage immediately for responsive UI
    if (typeof window !== 'undefined') {
      localStorage.setItem('center-app-theme-preference', newTheme);
    }

    // Update server preferences
    try {
      const success = await setTheme(newTheme);
      if (!success && toast.current) {
        toast.current.show({
          severity: 'warn',
          summary: 'Theme Updated',
          detail: 'Theme changed locally. Server sync will retry automatically.',
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Failed to update theme preference on server:', error);
      // Theme is already updated locally via localStorage, so user sees immediate change
    }
  };

  // The identity selector is populated once its options are built from the loaded
  // identity and one is selected. Surface that so the page can order the load.
  const identityLoading = !selectedIdentity || options.length === 0;
  useEffect(() => {
    onLoadingChange?.(identityLoading);
  }, [identityLoading, onLoadingChange]);

  // Timer to auto-collapse after 5 seconds, unless dropdown is open
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (isExpanded && !isDropdownOpen) {
      timer = setTimeout(() => {
        setIsExpanded(false);
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isExpanded, isDropdownOpen]);

  // Function to handle user interaction and expand the component
  const handleUserInteraction = () => {
    setIsExpanded(true);
  };

  // Fetch avatar image when component mounts or userId changes, via the global
  // sphere cache (intelligent batching + dedup). Self-contained so it can run
  // before the apiClients bail-out below (hooks must run unconditionally).
  useEffect(() => {
    const graphClient = apiClients?.graphClient;
    if (!userId || !graphClient) return;
    let cancelled = false;
    void (async () => {
      try {
        const cachedData = await globalSphereCache.getSphereData(userId, graphClient);
        if (!cancelled) setAvatarUrl(cachedData.thumbnailUrl ?? null);
      } catch {
        if (!cancelled) setAvatarUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, apiClients]);

  useEffect(() => {
    if (!selectedIdentity && options.length === 0 && !isFetchedRef.current && identity) {
      const identityOptions: Option[] = [];

      // Step 1: Add the identity entry as the first item in the options list
      const globalIdentity = identity.identity;

      // Set the metasphere identity ID
      setMetasphereIdentity(globalIdentity.id);

      if ((globalIdentity as any).defaultUserId?.[0] === identityId) {
        setIsFavoriteSelected(true);
      } else {
        setIsFavoriteSelected(false);
      }

      identityOptions.push({
        name: globalIdentity.name[0] ? globalIdentity.name[0] : 'NAME NOT FOUND!',
        code: globalIdentity.id,
        glyph: globalIdentity.avatar,
        imageId: globalIdentity.imageId,
      });

      // Step 2: Iterate over users and format them correctly
      identity.users.forEach((user) => {
        const userName = user.name.length > 0 ? user.name[0] : user.id;
        let formattedName = userName;

        // Type guard to ensure `user.organization` is an array and not undefined
        const userOrganization = Array.isArray(user.organization) && user.organization.length > 0 ? user.organization[0] : undefined;

        // Format user name with organization name if available
        if (userOrganization) {
          const organizationName = userOrganization.name.length > 0 ? userOrganization.name[0] : '';
          formattedName = `${userName} @ ${organizationName}`;
        }

        identityOptions.push({
          name: formattedName,
          code: user.id,
          glyph: 'lucide:user',
          imageId: user.imageId?.[0],
          organizationId: userOrganization?.id,
        });
      });

      setOptions(identityOptions);

      // Step 3: Set the default identity option if `defaultId` is specified
      if (userId) {
        const existingOption = identityOptions.find((option) => option.code === userId);
        if (existingOption) {
          setSelectedIdentity(existingOption);
        }
      } else if (identity.identity.defaultUserId?.[0]) {
        const defaultOption = identityOptions.find((option) => option.code === identity.identity.defaultUserId?.[0]);
        if (defaultOption) {
          setSelectedIdentity(defaultOption);
        }
      } else if (identityOptions.length > 0) {
        // Fallback: Select the first identity if none is selected
        setSelectedIdentity(identityOptions[0]);
      }

      isFetchedRef.current = true;
    }
  }, [endpoint, selectedIdentity, options, identity]);

  // Auto-switch to the account that belongs to the org of the sphere in the URL.
  // Opening a deep link to another org's sphere should put you in your account for
  // that org (if you have one) — via the active organization — while STAYING on that sphere
  // (no navigate / no setSphereId, unlike a manual switch). Fires once per org
  // (guard ref) so it can't loop or fight a later manual switch.
  useEffect(() => {
    const graphClient = apiClients?.graphClient;
    if (!urlSphereId || !graphClient || options.length === 0 || !selectedIdentity) return;
    // `null` means the parent owns the URL sphere and it is still loading: wait for
    // it rather than issue a fetch the page is already making. `undefined` means
    // no parent supplies it, so fetch here.
    if (urlSphere === null) return;
    let cancelled = false;
    void (async () => {
      try {
        // Prefer what the parent already holds; it has usually loaded the URL sphere
        // and passing it here avoids even a cache lookup.
        //
        // Otherwise read through the shared cache, NOT `getSphereById`: this effect
        // re-runs whenever `options`/`selectedIdentity` settle during load, and the
        // guard ref below is only set once the read resolves — so a direct call fired
        // the same request two or three times per page load. `getSphereData` returns
        // the sphere's type and partition key, which is all this needs.
        let sphere: UrlSphereHint | undefined =
          urlSphere && urlSphere.id === urlSphereId ? urlSphere : undefined;
        if (!sphere) {
          const cached = await globalSphereCache.getSphereData(urlSphereId, graphClient);
          sphere = cached?.info?.sphere as UrlSphereHint | undefined;
        }
        if (!sphere || cancelled) return;
        const props = sphere.properties ?? {};

        // `properties.sphereId` is the partition key, which is the organization —
        // EXCEPT a center_organization sphere, whose partition is the metasphere,
        // so use its own `.id` instead.
        const orgId: string | undefined =
          props.type === 'center_organization' ? sphere.id : props.sphereId;
        if (!orgId || cancelled || autoSwitchedOrgRef.current === orgId) return;
        autoSwitchedOrgRef.current = orgId;

        const match = options.find(
          (o) => o.organizationId && o.organizationId === orgId
        );
        // No account in this org, or already in it — nothing to do.
        if (!match || match.code === selectedIdentity.code) return;

        if (match.organizationId) {
          setActiveOrganization(match.organizationId);
          resetSpherePermissionCache();
          resetPlatformAdminCache();
          resetOrgAdminCache();
        }
        if (cancelled) return;
        setSelectedIdentity(match);
        dispatch(setUserId({ userId: match.code }));
      } catch (error) {
        console.error('[UserIdentity] Auto org-switch failed:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlSphereId, urlSphere, options, selectedIdentity]);

  // All hooks are declared above; only now is it safe to bail out of rendering
  // when apiClients is unavailable (e.g. while the session is still resolving)
  // without changing hook order between renders.
  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;

  const showIdentitySwitchFailed = () => {
    toast.current?.show({
      severity: 'error',
      summary: t('identitySelector.switchFailed'),
      detail: t('identitySelector.switchFailedDetail'),
      life: 6000,
    });
  };

  /**
   * Fails closed: the selection only moves once the server confirms the organization. The
   * active organization is client state sent as a header on every request, so a switch never
   * touches the Descope session; the server re-validates it against graph membership, and the
   * membership list is re-read first (the one re-bootstrap) so an organization the person has
   * left since sign-in is refused here, with the existing message, rather than answering 403
   * `organization_not_in_world` on every later request. On failure the picker keeps its
   * previous value, says so, and nothing navigates.
   */
  const onIdentityChange = async (e: { value: any }) => {
    const newSelection = e.value;

    // Check if the selected option is a user (not the identity itself)
    const isSelectingUser = newSelection.code !== metasphereIdentity;

    // Only an option with a real organization id selects an organization; the identity row
    // and a user with no organization are identity-level browsing (no header is sent).
    const targetOrganizationId: string | null =
      isSelectingUser && newSelection.organizationId ? newSelection.organizationId : null;

    if (targetOrganizationId) {
      try {
        const memberOrganizationIds = await fetchMemberOrganizationIds(apiClients.organizationClient);
        if (!memberOrganizationIds.includes(targetOrganizationId)) {
          throw new Error('organization_not_in_world');
        }
      } catch (error) {
        console.error('Organization switch failed:', error);
        showIdentitySwitchFailed();
        return;
      }
    }
    setActiveOrganization(targetOrganizationId);
    resetSpherePermissionCache();
    resetPlatformAdminCache();
    resetOrgAdminCache();

    // The active organization is now the chosen one: remember the choice on the identity so the
    // next sign-in — in any browser — restores it (useAppInitialization) instead of the
    // default. A user with no organization is identity-level browsing that no sign-in can
    // restore, so it is not remembered. The switch has already happened, so a failed write
    // only costs the restore; it is reported, not undone.
    let selectionToRemember: IdentitySelection | null = { kind: 'self' };
    if (isSelectingUser) {
      selectionToRemember = newSelection.organizationId
        ? { kind: 'org', organizationId: newSelection.organizationId, userId: newSelection.code }
        : null;
    }
    if (metasphereIdentity && selectionToRemember) {
      saveIdentitySelection(apiClients.graphClient, metasphereIdentity, selectionToRemember).catch((error) => {
        console.warn('Could not remember the identity selection:', error);
      });
    }

    // Continue with normal flow
    setSelectedIdentity(newSelection);
    if (newSelection.code) {
      dispatch(setUserId({ userId: newSelection.code }));
      // Navigate straight to the destination route. Going to the bare `/sphere/:id`
      // matches SphereRedirector instead of MetaspacePage, so the whole page unmounted
      // (dropping its fetched sphere, blanking the header) and immediately remounted
      // when the redirector bounced to this exact URL. Same destination, one mount.
      //
      // The route is the single source of truth for the centered sphere — MetaspacePage
      // syncs it into Redux in a layout effect. Dispatching setSphereId here as well
      // made Redux briefly disagree with the URL (new sphere in the store, old sphere
      // still in the route), and that sync promptly reverted it. The resulting
      // new → old → new flip remounted the outgoing metaspace and re-fetched its whole
      // organism set for the sphere we were navigating away from.
      navigate(`/sphere/${newSelection.code}/metaspace/${FALLBACK_METASPACE_SYSTEM_NAME}`);
    }
  };

  const identityOptionTemplate = (option: any) => (
    <div className="identity-item">
      <SphereAvatar glyph={option.glyph} glyphColor={option.glyphColor} imageId={option.imageId} sphereId={option.code} />
      <span>{option.name}</span>
    </div>
  );

  const selectedIdentityTemplate = (option: any) => {
    if (option?.name) {
      // Show only the email portion (before ' @ OrgName') to save horizontal space
      // in the nav bar when expanded. Full name is preserved in the title tooltip.
      const displayName = option.name.includes(' @ ')
        ? option.name.split(' @ ')[0]
        : option.name;
      return (
        <div className="identity-item identity-item-value">
          <span title={option.name}>{displayName}</span>
        </div>
      );
    }
    return <span>{t('identitySelector.select')}</span>;
  };

  const handleLogout = async () => {
    resetSpherePermissionCache();
    resetPlatformAdminCache();
    resetOrgAdminCache();
    await logout();
    navigate('/', { replace: true });
  };

  const uploadAvatar = () => {
    if (hasUploadPermission) {
      setIsUploadDialogVisible(true);
    } else {
      toast.current?.show({
        severity: 'warn',
        summary: t('profile.error'),
        detail: t('profile.invalidFileTypeDetail'),
        life: 3000,
      });
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (file) {
      try {
        // Validate file type if necessary
        const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!allowedMimeTypes.includes(file.type)) {
          toast.current?.show({
            severity: 'error',
            summary: t('profile.invalidFileType'),
            detail: t('profile.invalidFileTypeDetail'),
            life: 3000,
          });
          return;
        }

        await uploadSphereAvatar(graphClient, userId!, file);

        toast.current?.show({
          severity: 'success',
          summary: t('profile.success'),
          detail: t('profile.avatarUploadSuccess'),
          life: 3000,
        });
        setIsUploadDialogVisible(false);
        // Cache was cleared by the wrapper; trigger a fresh fetch on next render.
        const freshData = await globalSphereCache.getSphereData(userId!, graphClient);
        if (freshData.thumbnailUrl) {
          setAvatarUrl(freshData.thumbnailUrl);
        }
      } catch (error) {
        console.error('Error uploading avatar:', error);
        toast.current?.show({
          severity: 'error',
          summary: t('profile.uploadError'),
          detail: t('profile.uploadErrorDetail'),
          life: 3000,
        });
      }
    }
  };

  // Define the icon components inside your UserIdentity component
  const CollapseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37 38" fill="none">
      <g id="intbound dimension">
        <path
          id="Vector"
          d="M17.8121 15.8349C17.7346 15.7341 17.6169 15.6495 17.4719 15.5904C17.3269 15.5313 17.1602 15.5 16.9902 15.5C16.8203 15.5 16.6536 15.5313 16.5086 15.5904C16.3635 15.6495 16.2458 15.7341 16.1683 15.8349L11.089 22.6073C11.0182 22.701 10.9845 22.8052 10.991 22.9101C10.9976 23.015 11.0443 23.1169 11.1267 23.2064C11.209 23.2958 11.3243 23.3698 11.4616 23.4211C11.5988 23.4725 11.7535 23.4997 11.9109 23.5H22.0695C22.2269 23.4997 22.3816 23.4725 22.5189 23.4211C22.6561 23.3698 22.7714 23.2958 22.8538 23.2064C22.9361 23.1169 22.9828 23.015 22.9894 22.9101C22.996 22.8052 22.9623 22.701 22.8914 22.6073L17.8121 15.8349Z"
          stroke="#377FC4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );

  const ExpandIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37 38" fill="none">
      <g id="intbound dimension">
        <path
          id="Vector"
          d="M19.8121 22.1651C19.7346 22.2659 19.6169 22.3505 19.4719 22.4096C19.3269 22.4687 19.1602 22.5 18.9902 22.5C18.8203 22.5 18.6536 22.4687 18.5086 22.4096C18.3635 22.3505 18.2458 22.2659 18.1683 22.1651L13.089 15.3927C13.0182 15.299 12.9845 15.1948 12.991 15.0899C12.9976 14.985 13.0443 14.8831 13.1267 14.7936C13.209 14.7042 13.3243 14.6302 13.4616 14.5789C13.5988 14.5275 13.7535 14.5003 13.9109 14.5H24.0695C24.2269 14.5003 24.3816 14.5275 24.5189 14.5789C24.6561 14.6302 24.7714 14.7042 24.8538 14.7936C24.9361 14.8831 24.9828 14.985 24.9894 15.0899C24.996 15.1948 24.9623 15.299 24.8914 15.3927L19.8121 22.1651Z"
          stroke="#377FC4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );

  const TriangleIcon = () => (isDropdownOpen ? <CollapseIcon /> : <ExpandIcon />);

  const handleSetDefaultUser = async () => {
    try {
      if (!metasphereIdentity) {
        throw new Error('Metasphere identity ID is missing');
      }

      if (!identityId || !userId) {
        throw new Error('Missing identity ID or user ID');
      }

      // Use the refactored setDefaultUserForIdentity helper with apiClient
      const result = await setDefaultUserForIdentity(graphClient, metasphereIdentity, userId);

      if (result.success) {
        dispatch(setFavoriteUser({ userId: userId }));
        setIsFavoriteSelected(true);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: t('profile.error'),
        detail: error.response?.data?.error ?? t('profile.updateFailedDetail'),
        life: 3000,
      });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      {isImpersonating && (
        <output className="impersonation-banner" data-testid="impersonation-banner">
          {t('identitySelector.impersonationBanner', { actor: actorIdentityId, target: targetIdentityId })}
        </output>
      )}
      <div className="identity-dropdown" onClick={handleUserInteraction} style={{ cursor: 'pointer' }}>
        <div className="identity-dropdown-container">
          <div className="avatar-container">
            <div
              className="avatar-wrapper"
              onClick={hasUploadPermission && isExpanded ? uploadAvatar : undefined}
              style={{
                cursor: hasUploadPermission && isExpanded ? 'pointer' : 'default',
              }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={t('profile.avatarAlt')} className="avatar-image" width={32} height={32} />
              ) : (
                <PrimeReactButton
                  label=""
                  aria-label={t('profile.uploadPicture')}
                  className="identity-circle"
                  icon="user-icon"
                  raised
                  onClick={hasUploadPermission && isExpanded ? uploadAvatar : undefined}
                  disabled={!hasUploadPermission || !isExpanded}
                />
              )}
              {hasUploadPermission && isExpanded && (
                <div className="camera-overlay">
                  <i className="pi pi-camera"></i>
                </div>
              )}
            </div>
          </div>
          <PrimeReactSingleSelect
            value={selectedIdentity || { name: '', code: '' }}
            options={options}
            onChange={onIdentityChange}
            placeholder={selectedIdentity?.name ?? t('identitySelector.placeholder')}
            ariaLabel={t('identitySelector.select')}
            optionLabel="name"
            filter={true}
            valueTemplate={isExpanded ? selectedIdentityTemplate : undefined}
            itemTemplate={identityOptionTemplate}
            disabled={!isExpanded}
            className={isExpanded ? '' : 'collapsed-dropdown'}
            onShow={() => {
              setIsDropdownOpen(true);
            }}
            onHide={() => {
              setIsDropdownOpen(false);
            }}
            onMouseDown={(event) => {
              if (!isExpanded) {
                event.preventDefault();
                event.stopPropagation();
                setIsExpanded(true);
              }
            }}
            dropdownIcon={<TriangleIcon />}
          />
          {isExpanded && (
            <>
              <div className="favorite-icon-container">
                <FavoriteSelectorAtom isSelected={isFavoriteSelected} className="user-identity-star" onClick={handleSetDefaultUser} />
              </div>
              <button
                type="button"
                className="cog-icon-container"
                onClick={() => {
                  if (!userId) return;
                  navigate(`/sphere/${userId}/metaspace/center_identity_management_metaspace`);
                }}
              >
                <i className="pi pi-cog"></i>
              </button>
              <div
                className="theme-container"
                role="button"
                tabIndex={0}
                onClick={toggleTheme}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleTheme()}
              >
                <i className={`pi ${isDarkTheme ? 'pi-sun' : 'pi-moon'}`}></i>
              </div>

              <div
                className="logout-icon-container"
                role="button"
                tabIndex={0}
                aria-label={t('identitySelector.signOut')}
                onClick={handleLogout}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleLogout()}
              >
                <i className="pi pi-sign-out"></i>
              </div>
            </>
          )}
        </div>
      </div>

      <UploadAvatarDialogMolecule
        visible={isUploadDialogVisible}
        onHide={() => setIsUploadDialogVisible(false)}
        onUpload={handleFileUpload}
      />
    </>
  );
};

export default UserIdentity;
